<template>
    <div class="container">
        <div v-if="loading">
            <b-loading :is-full-page="false" :active="loading" :can-cancel="true"></b-loading>
        </div>
        <!-- Toggle for multiple payment methods -->
        <div class="field mb-4" v-if="multipleSegments">
            <b-field>
                <b-switch :rounded="true" v-model="multiplePaymentMethods" @input="useMultiplePaymentMethods">
                    Use multiple payment methods
                </b-switch>
            </b-field>
        </div>

        <!-- One payment method -->
        <div class="field pb-2" v-if="!customPaymentChosen && !multiplePaymentMethods">
            <div class="fieldsetBox">
                <div class="boxHeader">
                    <div class="boxHeaderContent">
                        <span class="textOnly">
                            <b-icon icon="credit-card" size=""></b-icon>
                        </span>
                    </div>
                </div>
                <div class="boxContent">
                    <PaymentMethodSelectBox :payments="payments" @paymentSelected="selectSinglePayment"
                        :selectedPayment="selectedSinglePayment" />
                </div>
            </div>
        </div>
        <!-- /One payment method -->

        <!-- Multiple payment methods -->
        <div class="field pb-2" v-if="!customPaymentChosen && multiplePaymentMethods">
            <div class="columns">

                <!-- Flight Payment -->
                <div class="column" v-if="pricingInfo.flight">
                    <div class="fieldsetBox">
                        <div class="boxHeader">
                            <div class="boxHeaderContent">
                                <span class="textOnly">
                                    <SegmentIcon type="air"></SegmentIcon>
                                </span>
                            </div>
                        </div>
                        <div class="boxContent">
                            <div class="boxSubHeading">
                                <div class="itemized-container" v-for="(flight, index) in pricingInfo.flight"
                                    :key="index">
                                    <span class="has-text-weight-semibold itemized-col">{{ flight.message }}</span>
                                    <span class="is-pulled-right itemized-col">{{ flight.total | formatPriceFromAmount(0, userDefaultCurrency)
                                        }}</span>
                                </div>
                            </div>
                            <PaymentMethodSelectBox :payments="airPayments" @paymentSelected="selectAirPayment" />
                        </div>
                    </div>
                </div>

                <!-- Hotel Payment -->
                <div class="column" v-if="pricingInfo.hotel">
                    <div class="fieldsetBox">
                        <div class="boxHeader">
                            <div class="boxHeaderContent">
                                <span class="textOnly">
                                    <SegmentIcon type="hotel"></SegmentIcon>
                                </span>
                            </div>
                        </div>
                        <div class="boxContent">
                            <div class="boxSubHeading">
                                <div class="itemized-container">
                                    <span class="has-text-weight-semibold itemized-col">{{ pricingInfo.hotel.message
                                        }}</span>
                                    <span class="is-pulled-right itemized-col">{{ pricingInfo.hotel.total |
                                        formatPriceFromAmount(0, userDefaultCurrency) }}</span>
                                </div>
                            </div>
                            <PaymentMethodSelectBox :payments="hotelPayments" @paymentSelected="selectHotelPayment" />
                            <div class="boxSubHeading" v-if="hotelPaymentPolicyMessage">
                                <p class="is-size-7">*{{ hotelPaymentPolicyMessage }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Car Payment -->
                <div class="column" v-if="car">
                    <div class="fieldsetBox">
                        <div class="boxHeader">
                            <div class="boxHeaderContent">
                                <span class="textOnly">
                                    <SegmentIcon type="car"></SegmentIcon>
                                </span>
                            </div>
                        </div>
                        <div class="boxContent">
                            <div class="boxSubHeading">
                                <div class="itemized-container">
                                    <span class="has-text-weight-semibold itemized-col">{{ pricingInfo.car.message
                                        }}</span>
                                    <span class="is-pulled-right itemized-col">{{ pricingInfo.car.total |
                                        formatPriceFromAmount(0, userDefaultCurrency) }}</span>
                                </div>
                            </div>
                            <PaymentMethodSelectBox :payments="carPayments" @paymentSelected="selectCarPayment" />
                            <div class="boxSubHeading">
                                <p class="is-size-7">*Card will be used as a guarantee and will not be charged immediately.</p>
                            </div>
                        </div>
                    </div>
                </div>
                

            </div>
        </div>
        <!-- /Multiple payment methods -->

    </div>
</template>

<script>
import PaymentMethodSelectBox from "./PaymentMethodSelectBox";
import SegmentIcon from "../UI/SegmentIcon.vue";
import { select } from "underscore";

export default {
    name: "ToggleSingleOrMultiPaymentMethod",
    components: {
        PaymentMethodSelectBox,
        SegmentIcon
    },
    props: {
        payments: {
            type: Array,
            required: true
        },
        customPaymentChosen: {
            type: Boolean,
            default: false
        },
        pricingInfo: {
            type: Object
        },
        hotel: {
            type: Object,
            default: null
        },
        car: {
            type: Object,
            default: null
        },
        selectedPayment: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            selectedSinglePayment: this.selectedPayment, //When new card is added it's auto selected
            multiplePaymentMethods: false,
            selectedAirPayments: [],
            selectedHotelPayments: [],
            selectedCarPayments: [],
            selectedPayments: [],
        };
    },
    watch: {
        selectedPayment(newVal) {
            // Update only if the prop changes
            if (newVal !== this.selectedSinglePayment) {
                this.selectedSinglePayment = newVal;  // Sync local state with prop
            }
        },
        airPayments(newAirPayments) {
            this.checkForEmptySegments(newAirPayments, this.hotelPayments, this.carPayments);
        },
        hotelPayments(newHotelPayments) {
            this.checkForEmptySegments(this.airPayments, newHotelPayments, this.carPayments);
        },
        carPayments(newCarPayments) {
            this.checkForEmptySegments(this.airPayments, this.hotelPayments, newCarPayments);
        },
        customPaymentChosen(newVal) {
            if (newVal && this.multiplePaymentMethods == true) {
                // Reset selections when custom payment is chosen
                this.selectedAirPayments = [];
                this.selectedHotelPayments = [];
                this.selectedCarPayments = [];

                // Emit the change
                this.$emit('paymentsSelected', []);
            }
        },
        multipleSegments(newVal) {
            // If there's only one segment left, automatically switch to single payment method
            if (!newVal && this.multiplePaymentMethods) {
                this.multiplePaymentMethods = false;
                this.useMultiplePaymentMethods(false);
            }
        },

    },
    methods: {
        selectSinglePayment(payment) {
            this.selectedSinglePayment = payment;
            this.emitSelectedPayments();
        },

        selectAirPayment(payment) {
            this.selectedAirPayments = [payment];
            this.emitSelectedPayments();
        },

        selectHotelPayment(payment) {
            this.selectedHotelPayments = [payment];
            this.emitSelectedPayments();
        },

        selectCarPayment(payment) {
            this.selectedCarPayments = [payment];
            this.emitSelectedPayments();
        },

        useMultiplePaymentMethods(val) {

            //Reset selected payments when toggling between single and multiple payment methods
            this.selectedPayments = [];

            if (val) {
                this.selectedSinglePayment = null;
            } else {
                this.selectedAirPayments = [];
                this.selectedHotelPayments = [];
                this.selectedCarPayments = [];
            }

            this.$emit('paymentsSelected', []);
            this.$emit('useMultiplePaymentMethods', val);
        },
        transformSelectedPayments(selectedPayments) {

            const mergedPayments = [];
            const paymentMap = new Map();

            //Merge payment objects with same id, so non-identical properties can be carried over (ie: amount)
            const mergePayments = (existingPayment, newPayment) => {
                return {
                    ...existingPayment,
                    segments: [...new Set([...existingPayment.segments, ...newPayment.segments])]
                };
            };

            //Map payments to a map, so we can merge them by id and update segments accordingly
            const mapPayments = (payments, segment) => {
                payments.forEach(payment => {
                    const paymentId = payment.id;

                    if (paymentMap.has(paymentId)) {
                        const existingPayment = paymentMap.get(paymentId);
                        const mergedPayment = mergePayments(existingPayment, { ...payment, segments: [segment] });
                        paymentMap.set(paymentId, mergedPayment);
                    } else {
                        paymentMap.set(paymentId, {
                            ...payment,
                            segments: [segment],
                        });
                    }
                });
            };

            // Map air and hotel payments
            if (selectedPayments.hotel.length > 0) {
                mapPayments(selectedPayments.hotel, 'hotel');
            }

            if (selectedPayments.flight.length > 0) {
                mapPayments(selectedPayments.flight, 'flight');
            }

            // We only need to map car payments if car data is passed         
            if (this.car && selectedPayments.car.length > 0) {
                mapPayments(selectedPayments.car, 'car');
            }

            // Convert map to array
            paymentMap.forEach(payment => {
                mergedPayments.push(payment);
            });

            return mergedPayments;
        },
        emitSelectedPayments() {

            //Transform selected payments into a single array
            const transformedPayments = this.transformSelectedPayments(this.selectedPaymentObj)

            this.selectedPayments = transformedPayments;

            this.$emit('paymentsSelected', transformedPayments);
        },
        checkForEmptySegments(airPayments, hotelPayments, carPayments) {
            if (
                (this.pricingInfo.flight && airPayments.length === 0) ||
                (this.pricingInfo.hotel && hotelPayments.length === 0) ||
                (this.car && carPayments.length === 0)
            ) {
                this.multiplePaymentMethods = false;
                this.useMultiplePaymentMethods(false);
            }
        }


    },
    computed: {
        paymentOptionsBySegment() {
            return this.$store.getters["bookTravel/getPaymentOptionsBySegment"];
        },
        airPayments() {

            const paymentOptions = this.paymentOptionsBySegment;

            if (paymentOptions && Object.prototype.hasOwnProperty.call(paymentOptions, 'flight')) {
                return paymentOptions.flight; // Return the flight payments
            }

            return []; 
        },
        tripSegments() {
            return Object.fromEntries(
                Object.entries(this.pricingInfo).filter(([key, value]) => key !== 'total' && value !== false)
            );
        },
        multipleSegments() {
            // Check if there are multiple segments (flight, hotel, car (has to be passed))
            const activeSegments = [this.pricingInfo.flight, this.pricingInfo.hotel, this.car]
                .filter(Boolean);
            return activeSegments.length > 1 && this.payments.length > 1;
        },

        hotelGuaranteeType() {
            return this.hotel?.guaranteeType;
        },
        hotelPayments() {
            
            const paymentOptions = this.paymentOptionsBySegment;

            if (paymentOptions && Object.prototype.hasOwnProperty.call(paymentOptions, 'hotel')) {
                return paymentOptions.hotel;
            }

            return []; 
        },
       carPayments() {
            
            const paymentOptions = this.paymentOptionsBySegment;

            if (paymentOptions && Object.prototype.hasOwnProperty.call(paymentOptions, 'car')) {
                return paymentOptions.car;
            }

            return []; 
        },
        hotelPaymentPolicyMessage() {
            if (this.hotelGuaranteeType === 'Guarantee' || this.hotelGuaranteeType === 'GuaranteeRequired') {
                return 'Card will be used as a guarantee and will not be charged immediately.';
            }
            if (this.hotelGuaranteeType === 'Deposit' || this.hotelGuaranteeType === 'DepositRequired' || this.hotelGuaranteeType === 'Prepay') {
                return 'Card will be charged now.';
            }
            return null;
        },
        //Categorizes payment(s) into segment types
        selectedPaymentObj() {

            const selectedPaymentObj = {
                flight: this.selectedAirPayments,
                hotel: this.selectedHotelPayments,
            }

            // Only factor car payments if car data is passed / form of guarantee is required
            if(this.car){
                selectedPaymentObj.car = this.selectedCarPayments;
            }

            if (!this.multiplePaymentMethods) {

                for (const segment in this.tripSegments) {
                    if (this.tripSegments[segment]) {
                        selectedPaymentObj[segment] = [this.selectedSinglePayment];
                    }
                }
            }

            return selectedPaymentObj;

        },
        userDefaultCurrency(){
            return this.$store.getters["travellersAndTrips/getUserDefaultCurrency"];
        },
    },
};
</script>

<style scoped lang="scss">
.fieldsetBox{
    min-height: 100px;
}
.boxContent {
    padding: 0px;
}

.boxSubHeading {
    padding: 1rem;
    border-top: 1px solid #dbdbdb;
    border-bottom: 1px solid #dbdbdb;
    background: whitesmoke;
}

.itemized-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
}

.itemized-col {
    flex: auto;
    min-width: 0;
}

.itemized-col+.itemized-col {
    text-align: right;
    white-space: nowrap;
}
</style>